import { useDropdownPopover } from '@/shared-components/dropdown';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';

import { isCancelledPaymentEntry, isPaidOrCompletedStatus } from '../utils/paymentEntryState';

import PaymentFilter from './PaymentFilter';

function PaymentEntryActions({
  entry,
  isMenuOpen,
  isInvoiceLoading,
  onToggleActionMenu,
  onOpenSummary,
  onOpenInvoice,
  t,
}) {
  const handleOpenChange = (nextOpen) => {
    if (nextOpen !== isMenuOpen) onToggleActionMenu(entry.id);
  };
  const { rootRef, triggerRef, menuId, toggle, handleTriggerKeyDown } = useDropdownPopover({
    open: isMenuOpen,
    onOpenChange: handleOpenChange,
    idPrefix: `payment-entry-${entry.id}`,
  });

  return (
    <div className="dropdown-menu dropdown-menu--compact profile-payments__item-actions" ref={rootRef}>
      <button type="button" className="dropdown-menu__trigger" onClick={toggle} onKeyDown={handleTriggerKeyDown} aria-label={t('aria.payment_actions')} aria-haspopup="menu" aria-expanded={isMenuOpen} aria-controls={menuId} ref={triggerRef}>
        <ThreeDotsHorizontal className="dropdown-menu__icon" />
      </button>
      {isMenuOpen && (
        <div id={menuId} className="dropdown-menu__menu is-open" role="menu">
          <button type="button" className="dropdown-menu__item" onClick={() => onOpenSummary(entry)}>{t('actions.summary')}</button>
          {!isCancelledPaymentEntry(entry) && (
            <button type="button" className="dropdown-menu__item" onClick={() => onOpenInvoice(entry)} disabled={isInvoiceLoading}>
              {isInvoiceLoading ? t('actions.generating') : t('actions.invoice')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PaymentsSection({
  t,
  filter,
  filterOptions,
  onFilterChange,
  error,
  loading,
  entries,
  activeActionMenuId,
  onToggleActionMenu,
  onOpenSummary,
  onOpenInvoice,
  invoiceActionLoadingOrderId,
}) {
  return (
    <div className="profile-payments">
      <div className="profile-payments__header">
        <div className="profile-payments__heading-row">
          <Text className="profile-payments__heading">{t('payments.heading')}</Text>
          <PaymentFilter value={filter} options={filterOptions} onChange={onFilterChange} />
        </div>
        <Text className="profile-payments__note">{t('payments.note')}</Text>
      </div>
      {error && <Text className="profile-subscriptions__error">{error}</Text>}
      {loading ? (
        <div className="profile-payments__loading-state">
          <Loader inline className="profile-payments__loader" />
        </div>
      ) : entries.length > 0 ? (
        <ul className="profile-payments__list">
          {entries.map((entry) => {
            const isMenuOpen = activeActionMenuId === String(entry.id);
            const isInvoiceLoading = invoiceActionLoadingOrderId === String(entry?.orderId ?? '');

            return (
              <li key={entry.id} className="profile-payments__item">
                <div className="profile-payments__item-top">
                  <div className="profile-payments__item-header">
                    <Text className="profile-payments__item-description">{entry.description}</Text>
                    <Text className="profile-payments__item-date">{entry.dateLabel}</Text>
                  </div>
                  <PaymentEntryActions
                    entry={entry}
                    isMenuOpen={isMenuOpen}
                    isInvoiceLoading={isInvoiceLoading}
                    onToggleActionMenu={onToggleActionMenu}
                    onOpenSummary={onOpenSummary}
                    onOpenInvoice={onOpenInvoice}
                    t={t}
                  />
                </div>
                <div className="profile-payments__item-bottom">
                  <Text className="profile-payments__item-amount">{entry.amountLabel ?? 'Amount unavailable'}</Text>
                  {entry.status && <Text className={`profile-payments__item-status${isPaidOrCompletedStatus(entry.status) ? ' profile-payments__item-status--paid' : ''}`}>{entry.status}</Text>}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <Text className="profile-payments__empty">{t('empty.payments')}</Text>
      )}
    </div>
  );
}
